import { useState, useEffect } from "react";
import { useNavigate, useLocation, Link } from "react-router-dom";
import { toast } from "react-toastify";
import { CheckCircle, XCircle, Mail, Loader2, ArrowLeft } from "lucide-react";
import {
  verifyEmail,
  sendVerificationEmail,
} from "../api/apiFunction/userServices";
import { updatePageTitle } from "../utils/titleUtils";
import { LogoWithText } from "../components/Logo";
import AuthShowcase from "../components/pages/auth/AuthShowcase";

const VerifyEmail = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [code, setCode] = useState(["", "", "", "", "", ""]);
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resending, setResending] = useState(false);
  const [error, setError] = useState("");

  useEffect(() => {
    updatePageTitle("Verify Email");
    // Get email from location state (passed from Register page)
    const emailFromState = location.state?.email;
    if (emailFromState) {
      setEmail(emailFromState);
    } else {
      // If no email in state, redirect back to register
      toast.error("Please sign up first");
      navigate("/register");
    }
  }, [location, navigate]);

  const handleCodeChange = (index, value) => {
    // Only allow numbers
    if (value && !/^\d$/.test(value)) {
      return;
    }

    const newCode = [...code];
    newCode[index] = value;
    setCode(newCode);
    setError("");

    // Auto-focus next input
    if (value && index < 5) {
      const nextInput = document.getElementById(`code-${index + 1}`);
      if (nextInput) {
        nextInput.focus();
      }
    }
  };

  const handleKeyDown = (index, e) => {
    // Handle backspace to go to previous input
    if (e.key === "Backspace" && !code[index] && index > 0) {
      const prevInput = document.getElementById(`code-${index - 1}`);
      if (prevInput) {
        prevInput.focus();
      }
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pastedData = e.clipboardData.getData("text").trim();

    // Only allow 6 digits
    if (/^\d{6}$/.test(pastedData)) {
      const digits = pastedData.split("");
      const newCode = [...code];
      digits.forEach((digit, index) => {
        if (index < 6) {
          newCode[index] = digit;
        }
      });
      setCode(newCode);
      setError("");

      // Focus last input
      const lastInput = document.getElementById(`code-5`);
      if (lastInput) {
        lastInput.focus();
      }
    }
  };

  const handleVerify = async (e) => {
    e.preventDefault();

    const verificationCode = code.join("");

    if (verificationCode.length !== 6) {
      setError("Please enter the complete 6-digit code");
      return;
    }

    if (!email) {
      toast.error("Email not found. Please sign up again.");
      navigate("/register");
      return;
    }

    setLoading(true);
    setError("");

    try {
      const response = await verifyEmail(verificationCode, email);

      if (response?.status === 200 && response?.data?.success) {
        toast.success(response.data.message || "Email verified successfully!");

        // If token is provided, automatically log the user in
        const token = response.data?.data?.token;
        if (token) {
          // Store basic user data with token - user will be fetched on next page load
          const userData = {
            token: token,
            email: email,
            emailVerified: true,
            userId: response.data?.data?.userId,
          };
          localStorage.setItem("user", JSON.stringify(userData));

          // Redirect to login page which will handle full user data fetch
          // The login page will detect the token and redirect appropriately
          setTimeout(() => {
            navigate("/login", {
              state: {
                message: "Email verified successfully! Redirecting...",
                autoLogin: true,
              },
            });
          }, 1000);
        } else {
          // No token provided, redirect to login
          setTimeout(() => {
            navigate("/login", {
              state: {
                message: "Email verified successfully! You can now log in.",
              },
            });
          }, 2000);
        }
      } else {
        const errorMessage =
          response?.data?.message ||
          "Invalid verification code. Please try again.";
        setError(errorMessage);
        toast.error(errorMessage);
        // Clear code on error
        setCode(["", "", "", "", "", ""]);
        document.getElementById("code-0")?.focus();
      }
    } catch (error) {
      const errorMessage =
        error?.response?.data?.message ||
        "An error occurred during verification. Please try again.";
      setError(errorMessage);
      toast.error(errorMessage);
      // Clear code on error
      setCode(["", "", "", "", "", ""]);
      document.getElementById("code-0")?.focus();
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!email) {
      toast.error("Email not found. Please sign up again.");
      navigate("/register");
      return;
    }

    setResending(true);
    setError("");
    try {
      const response = await sendVerificationEmail(email);

      if (response?.status === 200 && response?.data?.success) {
        toast.success(
          response.data.message ||
            "Verification code sent successfully. Please check your inbox."
        );
        // Clear code inputs
        setCode(["", "", "", "", "", ""]);
        document.getElementById("code-0")?.focus();
      } else {
        toast.error(
          response?.data?.message ||
            "Failed to send verification code. Please try again."
        );
      }
    } catch (error) {
      toast.error("Failed to send verification code. Please try again.");
    } finally {
      setResending(false);
    }
  };

  return (
    <div className="h-screen bg-bg-50 flex overflow-hidden">
      {/* Left Side - Verification Form */}
      <div className="w-full lg:w-1/2 bg-bg-50 p-4 lg:p-8">
        <div className="bg-bg-70 rounded-3xl h-full flex items-center justify-center p-8 lg:p-12">
          <div className="w-full max-w-md space-y-8">
            {/* Logo */}
            <div className="text-center">
              <LogoWithText className="h-8 w-auto mx-auto mb-8" />
            </div>

            {/* Verification Icon */}
            <div className="flex justify-center">
              <div className="w-20 h-20 rounded-full bg-blue-500/20 flex items-center justify-center">
                <Mail className="h-10 w-10 text-blue-500" />
              </div>
            </div>

            {/* Title and Description */}
            <div className="text-center space-y-2">
              <h2 className="text-2xl font-semibold text-fg-50">
                Verify Your Email
              </h2>
              <p className="text-sm text-fg-60">
                We've sent a 6-digit verification code to
              </p>
              <p className="text-sm font-medium text-fg-50 break-all">
                {email}
              </p>
              <p className="text-xs text-fg-60 mt-2">
                Please enter the code below to verify your email address.
              </p>
            </div>

            {/* Error Message */}
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3">
                <p className="text-sm text-red-700 text-center">{error}</p>
              </div>
            )}

            {/* Verification Code Input */}
            <form onSubmit={handleVerify} className="space-y-6">
              <div className="flex justify-center gap-3">
                {code.map((digit, index) => (
                  <input
                    key={index}
                    id={`code-${index}`}
                    type="text"
                    inputMode="numeric"
                    maxLength={1}
                    value={digit}
                    onChange={(e) => handleCodeChange(index, e.target.value)}
                    onKeyDown={(e) => handleKeyDown(index, e)}
                    onPaste={index === 0 ? handlePaste : undefined}
                    className="w-12 h-14 text-center text-2xl font-bold bg-bg-60 border-0 rounded-lg text-fg-50 focus:outline-none focus:ring-2 focus:ring-ac-02 transition-all"
                    disabled={loading}
                    autoFocus={index === 0}
                  />
                ))}
              </div>

              {/* Verify Button */}
              <button
                type="submit"
                disabled={loading || code.join("").length !== 6}
                className={`w-full flex items-center justify-center gap-2 py-3 px-4 rounded-lg text-sm font-medium text-white dark:text-black transition-colors ${
                  loading || code.join("").length !== 6
                    ? "bg-ac-02 bg-opacity-50 cursor-not-allowed"
                    : "bg-ac-02 hover:bg-ac-01"
                } focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-ac-02`}
              >
                {loading ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Verifying...
                  </>
                ) : (
                  <>
                    <CheckCircle size={16} />
                    Verify Email
                  </>
                )}
              </button>
            </form>

            {/* Resend Code */}
            <div className="text-center space-y-3">
              <p className="text-sm text-fg-60">Didn't receive the code?</p>
              <button
                onClick={handleResendCode}
                disabled={resending}
                className="text-sm font-medium text-ac-02 hover:text-ac-01 transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 mx-auto"
              >
                {resending ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    Sending...
                  </>
                ) : (
                  <>
                    <Mail size={16} />
                    Resend Code
                  </>
                )}
              </button>
            </div>

            {/* Back to Register */}
            <div className="pt-4 border-t border-bd-50">
              <Link
                to="/register"
                className="flex items-center justify-center gap-2 text-sm text-fg-60 hover:text-fg-50 transition-colors"
              >
                <ArrowLeft size={16} />
                Back to Sign Up
              </Link>
            </div>
          </div>
        </div>
      </div>

      {/* Right Side - Showcase */}
      <AuthShowcase />
    </div>
  );
};

export default VerifyEmail;
